import React, { FC, useState, useEffect } from 'react';
import { useCheckPermission } from 'src/utils/checkPermission';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useDispatch, useSelector } from 'react-redux';
import { getRemindersListFetch } from 'src/resources/remindersList/reducer';
import Button from '../Button';
import translate from '@translate';
import { RootStoreType } from 'src/types/store';
import { Badge, Divider, ListSubheader } from '@mui/material';
import MUIMenu, { MenuProps } from '@mui/material/Menu';
import { colors, sizes } from 'src/themes/default';
import Typography from '../Typography';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '../IconButton';
import moment from 'moment';
import ReminderDetailContainer from 'src/containers/ReminderDetailContainer';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import { styled } from '@mui/material/styles';

export interface RemindersItemPropsType {
    type: 'order' | 'return' | 'claim' | 'receiving';
    idItem?: string;
}

const Menu = styled(MUIMenu)<MenuProps>(({ theme }) => ({
    '& .MuiList-root': {
        paddingTop: 0,
        paddingBottom: 0,

        '& .MuiListSubheader-root': {
            height: '48px',
            padding: '13px 16px',
            backgroundColor: colors.background_B2,

            '& h3': {
                marginRight: '60px',
            },
            '& button': {
                position: 'absolute',
                top: '0px',
                right: '0px',
                padding: '12px 16px',

                '&:hover svg': {
                    fill: colors.primary_O,
                },
            },
        },

        '& .MuiTypography-body1': {
            fontWeight: sizes.bold_W,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },

        '& .MuiMenuItem-root': {
            display: 'block',
        },
    },
}));

const RemindersItem: FC<RemindersItemPropsType> = (props) => {
    const dispatch = useDispatch();
    const permissionList = useCheckPermission('REMINDER.LIST');
    const permissionCreate = useCheckPermission('REMINDER.CREATE');
    const permissionDetail = useCheckPermission('REMINDER.DETAIL');
    const [openId, setOpenId] = useState<null | number>(null);
    const data = useSelector((state: RootStoreType) => state.remindersList.data);
    const [notificationsAnchorEl, setNotificationsAnchorEl] = React.useState<null | HTMLElement>(null);

    useEffect(() => {
        if (!permissionList || !props.idItem) {
            return;
        }
        dispatch(getRemindersListFetch({ typeItem: props.type, idItem: props.idItem }));
    }, [dispatch, permissionList, props.type, props.idItem]);

    if (!permissionList) {
        return null;
    }

    const handleNotificationsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setNotificationsAnchorEl(event.currentTarget);
    };

    const handleNew = () => {
        setOpenId(0);
    };

    return (
        <>
            <Button
                variant="outlined"
                tooltiop={translate('reminders.title')}
                onClick={handleNotificationsMenuOpen}
                data-testid={`reminders-item-${props.type}`}
            >
                <Badge
                    badgeContent={data?.length || 0}
                    color="error"
                    sx={{
                        '& .MuiBadge-badge': {
                            marginTop: '3px',
                            marginRight: '2px',
                            fontSize: '10px',
                            height: '16px',
                            minWidth: '11px',
                            lineHeight: '14px',
                            backgroundColor: colors.primary_O,
                        },
                    }}
                >
                    <NotificationsIcon />
                </Badge>
            </Button>
            <Menu
                anchorEl={notificationsAnchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                id={`primary-reminder-${props.type}`}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={!!notificationsAnchorEl}
                onClose={() => setNotificationsAnchorEl(null)}
            >
                <ListSubheader>
                    <Typography variant="h3" gutterBottom>
                        {translate('reminders.title')}
                    </Typography>
                    {permissionCreate && (
                        <IconButton noBackground tooltiop={translate('reminders.create')} onClick={handleNew}>
                            <NotificationAddIcon />
                        </IconButton>
                    )}
                </ListSubheader>
                {data?.map((item, index) => (
                    <div key={index}>
                        <Divider />
                        <MenuItem onClick={() => permissionDetail && setOpenId(item.ID || 0)}>
                            <Typography variant="body1" component={'div'}>
                                {item.TITLE}
                            </Typography>
                            <Typography variant="caption" component={'div'}>
                                {moment(item.EXECUTE_AT).format('LLL')}
                            </Typography>
                        </MenuItem>
                    </div>
                ))}
            </Menu>
            {openId !== null ? (
                <ReminderDetailContainer
                    idReminder={openId}
                    type={props.type}
                    onClose={() => setOpenId(null)}
                    setOpenId={(id: number) => setOpenId(id)}
                    idItem={props.idItem}
                />
            ) : null}
        </>
    );
};

export default RemindersItem;
